import { Box, CircularProgress, Grid } from '@mui/material';

import { FProgressBar } from '../../../../../../components/base';
import { disabledFutureDate } from '../../../../../../helpers';
import {
  CLIENT_SEX_OPTIONS,
  FormRadioGroupField,
  PRIMARY_LANGUAGE_OPTIONS,
  QuestionnaireSectionLabel,
} from '../../../../common';
import {
  FormAutocompleteField,
  FormCheckboxField,
  FormDateField,
  FormTextField,
} from '../../../../../common';
import { RELATIONSHIP_TO_CLIENT_OPTIONS } from './constants';
import { US_STATE_OPTIONS } from '../../../../../../constants';
import { useQuestionnaireFormik } from '../../../../common/hooks';
import { onboardingQuestionnaireActions } from '../../../../../../store';
import { ONBOARDING_TAB_IDS } from '../../../constants';
import {
  useOnboardingQuestionnaireLoading,
  useOnboardingQuestionnaireUpdateLoading,
} from '../../../hooks';
import {
  useClientProviderAddressSync,
  useGeneralInformationInitialValues,
  useSaveGeneralInformationFieldOnBlur,
} from './hooks';
import { GENERAL_INFORMATION_FORM_SX } from './styles';
import { GENERAL_INFO_FORM_SCHEMA } from './validationSchema';
import { FORTA_BLUE } from '../../../../../../utils/colors';

export default function GeneralInformationForm() {
  const initialValues = useGeneralInformationInitialValues();
  const isLoading = useOnboardingQuestionnaireLoading();
  const isUpdating = useOnboardingQuestionnaireUpdateLoading();
  const formik = useQuestionnaireFormik({
    tabId: ONBOARDING_TAB_IDS.GENERAL,
    initialValues,
    validationSchema: GENERAL_INFO_FORM_SCHEMA,
    updateTabStatus: onboardingQuestionnaireActions.updateOnboardingTabStatus,
  });
  const { saveFieldOnBlur, saveGeneralInfoFields } = useSaveGeneralInformationFieldOnBlur(
    formik,
    initialValues,
  );
  const {
    handleSameAsProviderChange,
    handleClientAddressFieldChange,
    saveClientAddressFieldOnBlur,
    saveClientAddressFieldOnChange,
  } = useClientProviderAddressSync({
    formik,
    saveFieldOnBlur,
    saveGeneralInfoFields,
  });

  return (
    <>
      <QuestionnaireSectionLabel variant="h5" sx={{ mb: 1.5, color: FORTA_BLUE.primary }}>
        General Information
      </QuestionnaireSectionLabel>
      <Box
        component="form"
        noValidate
        onSubmit={formik.handleSubmit}
        sx={GENERAL_INFORMATION_FORM_SX}
      >
        {isUpdating ? <FProgressBar /> : null}
        {isLoading ? (
          <Box
            sx={{
              alignItems: 'center',
              bgcolor: 'rgba(255, 255, 255, 0.65)',
              display: 'flex',
              inset: 0,
              justifyContent: 'center',
              position: 'absolute',
              zIndex: 1,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : null}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormTextField
              disabled={isLoading}
              formik={formik}
              label="Your name"
              name="provider_full_name"
              onBlur={saveFieldOnBlur}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormAutocompleteField
              disabled={isLoading}
              formik={formik}
              label="Primary language spoken in the home"
              name="primary_language"
              onChange={saveFieldOnBlur}
              options={PRIMARY_LANGUAGE_OPTIONS}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormTextField
              disabled={isLoading}
              formik={formik}
              label="Email address"
              name="provider_email"
              onBlur={saveFieldOnBlur}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormAutocompleteField
              disabled={isLoading}
              formik={formik}
              label="Relationship to child"
              name="relationship_to_client"
              onChange={saveFieldOnBlur}
              options={RELATIONSHIP_TO_CLIENT_OPTIONS}
              required
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <QuestionnaireSectionLabel variant="h6" sx={{ mb: { xs: 2, md: 0 } }}>
              Client information
            </QuestionnaireSectionLabel>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormTextField
              disabled={isLoading}
              formik={formik}
              label="Child legal first name"
              name="client_first_name"
              onBlur={saveFieldOnBlur}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormTextField
              disabled={isLoading}
              formik={formik}
              label="Child legal last name"
              name="client_last_name"
              onBlur={saveFieldOnBlur}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormTextField
              disabled={isLoading}
              formik={formik}
              label="Child legal middle name"
              name="client_middle_name"
              onBlur={saveFieldOnBlur}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormDateField
              disabled={isLoading}
              disabledDate={disabledFutureDate}
              formik={formik}
              label="Child date of birth"
              name="client_date_of_birth"
              onChange={saveFieldOnBlur}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormRadioGroupField
              disabled={isLoading}
              formik={formik}
              label="Child gender"
              name="client_sex"
              onChange={saveFieldOnBlur}
              options={CLIENT_SEX_OPTIONS}
              required
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
            <FormCheckboxField
              disabled={isLoading}
              formik={formik}
              label="Client address same as provider"
              name="client_address_same_as_provider"
              onChange={handleSameAsProviderChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormTextField
              disabled={isLoading}
              formik={formik}
              label="Child street address line one"
              name="client_street_address_line_one"
              onBlur={saveClientAddressFieldOnBlur}
              onChange={handleClientAddressFieldChange}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormTextField
              disabled={isLoading}
              formik={formik}
              label="Child address line two (Apartment/unit)"
              name="client_street_address_line_two"
              onBlur={saveClientAddressFieldOnBlur}
              onChange={handleClientAddressFieldChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <FormTextField
              disabled={isLoading}
              formik={formik}
              label="Child city"
              name="client_street_address_city"
              onBlur={saveClientAddressFieldOnBlur}
              onChange={handleClientAddressFieldChange}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormAutocompleteField
              disabled={isLoading}
              formik={formik}
              label="Child state"
              name="client_street_address_state"
              onChange={saveClientAddressFieldOnChange}
              options={US_STATE_OPTIONS}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormTextField
              disabled={isLoading}
              formik={formik}
              label="Child zip code"
              name="client_street_address_zip"
              onBlur={saveClientAddressFieldOnBlur}
              onChange={handleClientAddressFieldChange}
              required
            />
          </Grid>
        </Grid>
      </Box>
    </>
  );
}
